import * as R from 'ramda'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { toLocale } from '$func/text'
import { CreateInviteLinkInput } from './create-invite-link-input'
import { NumberInput } from '$common/input'
import { t } from '$i18n'

interface InviteLinkUsageInputProps {
  usageLimit?: number
}

interface UsageMark {
  value: number
  label: string
  usage: number
}

export interface CreateInviteLinkUsageInputApiType {
  getValue: () => number
}

function generateUsageMarks(usage: number) {
  return (R.pipe(
    R.sort(R.subtract),
    R.uniq,
    R.addIndex<number>(R.map)((value, index) => ({
      value: index,
      label:
        value === Infinity
          ? t('inviteLinks.common.unlimited')
          : toLocale(value),
      usage: value,
    })),
  )([1, 10, 100, Infinity, usage]) as unknown) as UsageMark[]
}

export const CreateInviteLinkUsageInput = forwardRef(
  function CreateInviteLinkUsageInput(
    { usageLimit }: InviteLinkUsageInputProps,
    ref,
  ) {
    const [usage, setUsage] = useState<number>(usageLimit ?? Infinity)

    useImperativeHandle(
      ref,
      () =>
        ({
          getValue: () => usage,
        } as CreateInviteLinkUsageInputApiType),
      [usage],
    )

    const marks = generateUsageMarks(usage)

    const [textInputValue, setNumberInputValue] = useState('')

    const onNumberInputValueChange = (value: number) => {
      setNumberInputValue(value === 0 ? '' : toLocale(value))
      /* Check for empty value (default set to infinity) */
      if (value === 0 || value > 100000) setUsage(Infinity)
      else setUsage(value)
    }

    return (
      <CreateInviteLinkInput
        marks={marks}
        value={R.findIndex(R.propEq('usage', usage), marks)}
        onChange={(value: number) => setUsage(marks[value].usage)}
        subheader={t('inviteLinks.create.usageLimit.subheader')}
        description={t('inviteLinks.create.usageLimit.description')}
        inputComponent={
          <NumberInput
            placeholder={t('inviteLinks.create.usageLimit.inputPlaceholder')}
            value={textInputValue}
            onChange={onNumberInputValueChange}
            onBlur={() => setNumberInputValue('')}
          />
        }
      />
    )
  },
)
